import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useRedactTicketText } from "../../hooks/useTickets";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";

// What the server turns each redacted character into
const REDACTION_MARK = "▇";

interface RedactTextDialogProps {
  ticketId: string;
  // The comment to redact from; omitted for the ticket's description
  commentId?: string;
  // The comment's or description's current text, for the preview
  content: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Permanently redacts text from a comment or a ticket's description (admins). The admin pastes the exact text; the
 * preview shows the result, and the server also scrubs it from history, audit entries and emails. There is no undo.
 */
export const RedactTextDialog: React.FC<RedactTextDialogProps> = ({
  ticketId,
  commentId,
  content,
  open,
  onOpenChange,
}) => {
  const [text, setText] = useState("");
  const redactMutation = useRedactTicketText();

  const occurrences = useMemo(() => (text ? content.split(text).length - 1 : 0), [content, text]);
  const preview = useMemo(
    () => (occurrences > 0 ? content.split(text).join(REDACTION_MARK.repeat(text.length)) : content),
    [content, text, occurrences]
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) setText("");
    onOpenChange(next);
  };

  const handleRedact = async () => {
    try {
      const result = await redactMutation.mutateAsync({ ticketId, commentId, text });
      showToast.success(
        "Text Redacted",
        result.occurrences === 1 ? "The text was redacted." : `The text was redacted in ${result.occurrences} places.`
      );
      handleOpenChange(false);
    } catch (error) {
      showToast.error("Redaction Failed", getErrorMessage(error, "Failed to redact the text. Please try again."));
    }
  };

  const target = commentId ? "comment" : "description";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Redact text from this {target}</DialogTitle>
          <DialogDescription>
            Use this for data that should never have been sent, like a card number or a password. Each character is
            replaced with {REDACTION_MARK}, here and in the ticket's history, the audit log and the emails that carried
            it. This can't be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="redact-text">Text to redact</Label>
            <Textarea
              id="redact-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the exact text, including capitalization"
              rows={2}
              maxLength={1000}
              autoFocus
            />
            {text && occurrences === 0 && (
              <p className="text-sm text-red-600 dark:text-red-400">That text doesn't appear in this {target}.</p>
            )}
          </div>

          {occurrences > 0 && (
            <div className="space-y-2">
              <Label>
                Preview ({occurrences} {occurrences === 1 ? "match" : "matches"})
              </Label>
              <p className="max-h-48 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap break-words">
                {preview}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRedact}
            disabled={occurrences === 0 || redactMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {redactMutation.isPending ? "Redacting..." : "Redact Permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
